/**
 * Histogram bins.
 *
 * Three textbook rules suggest a bin width, or the reader sets one:
 *
 * - **Sturges (1926)**: k = ⌈log₂ n⌉ + 1 bins across the range.
 * - **Scott (1979)**: h = 3.49 · s · n^(−1/3), with s the sample standard deviation.
 * - **Freedman–Diaconis (1981)**: h = 2 · IQR · n^(−1/3), with the IQR from inclusive
 *   (Excel `QUARTILE.INC`, R type 7) quartiles.
 *
 * With `roundWidth`, the suggested width is replaced by the nearest convenient width
 * (1, 2, 2.5 or 5 × 10ᵏ) and the first edge moved down to a multiple of it — the way a
 * statistics course builds a frequency table. Without it, the bins start at the minimum
 * and use the rule's width exactly.
 *
 * Every bin includes its lower edge and excludes its upper edge, [a, b), except the
 * last, which includes both, [a, b], so the maximum is always counted. That is NumPy's
 * convention.
 */

import { quantile, standardDeviation } from "@/lib/stats/descriptive";
import {
  extent,
  nearestNiceWidth,
  roundTo,
  stepDecimals,
} from "@/lib/tool-kit/charts/scale";

export type BinRule = "sturges" | "scott" | "freedman-diaconis" | "manual";

export interface BinOptions {
  rule: BinRule;
  /** Round the suggested width to a convenient number (ignored for `manual`). */
  roundWidth?: boolean;
  /** Bin width, for `manual`. */
  width?: number | null;
  /** First bin edge, for `manual`. Defaults to a multiple of the width. */
  start?: number | null;
}

export interface Histogram {
  /** Bins Sturges' rule suggests (Sturges only). */
  suggestedBins: number | null;
  /** The width the rule produced before any rounding. */
  ruleWidth: number;
  /** The width used. */
  width: number;
  /** n + 1 edges for n bins. */
  edges: number[];
  counts: number[];
  /** Values left out because they are below the first edge (manual start only). */
  below: number;
  /** True when the rule gave no usable width and Sturges was used instead. */
  fellBack: boolean;
}

export type HistogramError = "noData" | "badWidth" | "tooManyBins";

/** The most bins drawn; a width that would need more is refused. */
export const MAX_BINS = 200;

function sturgesBins(n: number): number {
  return Math.ceil(Math.log2(n)) + 1;
}

/** The width a rule suggests, or null when the data give it nothing to work with. */
function ruleWidth(
  rule: Exclude<BinRule, "manual">,
  values: readonly number[],
  range: number
): number | null {
  const n = values.length;
  if (rule === "scott") {
    const s = standardDeviation(values);
    return s > 0 ? 3.49 * s * n ** (-1 / 3) : null;
  }
  if (rule === "freedman-diaconis") {
    const iqr =
      quantile(values, 0.75, "inclusive") - quantile(values, 0.25, "inclusive");
    return iqr > 0 ? 2 * iqr * n ** (-1 / 3) : null;
  }
  return range / sturgesBins(n);
}

/** Count values into [a, b) bins, the last one closed. */
export function countIntoBins(
  values: readonly number[],
  edges: readonly number[]
): { counts: number[]; below: number } {
  const counts = new Array<number>(Math.max(0, edges.length - 1)).fill(0);
  let below = 0;
  const last = edges.length - 1;
  for (const value of values) {
    if (value < edges[0]) {
      below += 1;
      continue;
    }
    if (value >= edges[last]) {
      counts[last - 1] += value === edges[last] ? 1 : 0;
      continue;
    }
    // Binary search for the bin whose lower edge is the last one ≤ value.
    let low = 0;
    let high = last - 1;
    while (low < high) {
      const middle = Math.ceil((low + high) / 2);
      if (edges[middle] <= value) {
        low = middle;
      } else {
        high = middle - 1;
      }
    }
    counts[low] += 1;
  }
  return { counts, below };
}

function buildEdges(
  start: number,
  width: number,
  max: number,
  exactLast: boolean
): number[] | null {
  const decimals = Math.min(12, stepDecimals(width) + stepDecimals(start));
  const bins = Math.max(1, Math.ceil((max - start) / width - 1e-9));
  if (bins > MAX_BINS) {
    return null;
  }
  const edges: number[] = [];
  for (let i = 0; i <= bins; i++) {
    edges.push(roundTo(start + i * width, decimals));
  }
  if (exactLast) {
    edges[bins] = max;
  } else if (edges[bins] < max) {
    edges.push(roundTo(start + (bins + 1) * width, decimals));
  }
  return edges;
}

/** A single bin one unit wide, for data with no spread. */
function singleBin(value: number): number[] {
  const start = Math.floor(value);
  return start === value ? [value - 0.5, value + 0.5] : [start, start + 1];
}

/** The largest multiple of `width` at or below `value`. */
function snapDown(value: number, width: number): number {
  return roundTo(Math.floor(value / width + 1e-9) * width, stepDecimals(width));
}

interface BinPlan {
  /** The rule's width before rounding (0 when the data have no spread). */
  raw: number;
  width: number;
  start: number;
  fellBack: boolean;
}

/** Width and first edge from a rule, or from the reader for `manual`. */
function planBins(
  values: readonly number[],
  options: BinOptions,
  [min, max]: [number, number]
): BinPlan | "badWidth" {
  if (options.rule === "manual") {
    const width = options.width ?? 0;
    if (!(width > 0)) {
      return "badWidth";
    }
    return {
      raw: width,
      width,
      start: options.start ?? snapDown(min, width),
      fellBack: false,
    };
  }
  let suggested = ruleWidth(options.rule, values, max - min);
  const fellBack = suggested === null && options.rule !== "sturges";
  if (suggested === null) {
    suggested = ruleWidth("sturges", values, max - min);
  }
  const raw = suggested ?? 0;
  if (!(raw > 0)) {
    return { raw: 0, width: 0, start: min, fellBack };
  }
  const width = options.roundWidth ? nearestNiceWidth(raw) : raw;
  const start = options.roundWidth ? snapDown(min, width) : min;
  return { raw, width, start, fellBack };
}

export function buildHistogram(
  values: readonly number[],
  options: BinOptions
): Histogram | HistogramError {
  const range = extent(values);
  if (!range) {
    return "noData";
  }
  const [min, max] = range;
  const plan = planBins(values, options, range);
  if (plan === "badWidth") {
    return plan;
  }
  const { raw, width, start, fellBack } = plan;
  if (width === 0) {
    const edges = singleBin(min);
    return {
      suggestedBins: options.rule === "sturges" || fellBack ? 1 : null,
      ruleWidth: 0,
      width: edges[1] - edges[0],
      edges,
      ...countIntoBins(values, edges),
      fellBack,
    };
  }
  const usesSturges = options.rule === "sturges" || fellBack;
  // Unrounded Sturges divides the range into exactly k bins, so its last edge IS the
  // maximum; pin it there rather than trusting k × (range ÷ k) to land on it.
  const exactLast = usesSturges && !options.roundWidth;
  const edges = buildEdges(start, width, Math.max(max, start), exactLast);
  if (!edges) {
    return "tooManyBins";
  }
  return {
    suggestedBins: usesSturges ? sturgesBins(values.length) : null,
    ruleWidth: raw,
    width,
    edges,
    ...countIntoBins(values, edges),
    fellBack,
  };
}
